import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ServiceConfig } from '../types';

const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: Math.random() * 3.5 + 1,
  delay: Math.random() * 5,
  duration: Math.random() * 8 + 6,
}));

interface Props {
  config: Pick<ServiceConfig, 'heroBadge' | 'heroTitle' | 'heroHighlight' | 'heroLocation' | 'heroDesc' | 'heroImageUrl'>;
}

export default function ServiceHero({ config }: Props) {
  const [loaded, setLoaded] = useState(false);
  const [lineReady, setLineReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const t1 = setTimeout(() => setLoaded(true), 100);
    const t2 = setTimeout(() => setLineReady(true), 700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <section
      id="top"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0A1628]"
    >
      <div className="absolute inset-0">
        <img
          src={config.heroImageUrl}
          alt={config.heroTitle}
          className="w-full h-full object-cover object-top opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628] via-[#0A1628]/90 to-[#0A1628]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-transparent to-[#0A1628]/50" />
      </div>

      {PARTICLES.map((p) => (
        <span
          key={p.id}
          className="absolute rounded-full bg-[#C8A96A] pointer-events-none"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size,
            opacity: 0.25,
            animation: `floatParticle ${p.duration}s ${p.delay}s ease-in-out infinite alternate`,
          }}
        />
      ))}

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-28 pb-20">
        <button
          onClick={() => navigate('/')}
          className={`inline-flex items-center gap-1.5 text-[#C8A96A]/60 hover:text-[#C8A96A] text-xs mb-8 transition-all duration-300 cursor-pointer group ${loaded ? 'opacity-100' : 'opacity-0'}`}
        >
          <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform duration-200" />
          Back to Quantum Electrical
        </button>

        <div
          className={`inline-flex items-center gap-2 border border-[#C8A96A]/40 rounded-full px-4 py-1.5 mb-6 transition-all duration-700 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-[#C8A96A] animate-pulse" />
          <span className="text-[#C8A96A] text-xs tracking-widest uppercase font-medium">
            {config.heroBadge}
          </span>
        </div>

        <h1 className="font-playfair leading-tight mb-6 max-w-3xl">
          <span
            className={`block text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl transition-all duration-700 delay-100 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {config.heroTitle}
          </span>

          <span className="block relative overflow-hidden">
            <span
              className={`block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold transition-all duration-700 delay-200 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{
                backgroundImage: 'linear-gradient(90deg, #C8A96A, #E0C78A, #C8A96A, #A8894F)',
                backgroundSize: '300% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: loaded ? 'goldShimmer 4s ease-in-out infinite' : 'none',
              }}
            >
              {config.heroHighlight}
            </span>
            <span
              className="absolute bottom-0 left-0 h-0.5 rounded-full transition-all duration-1000 delay-500"
              style={{
                width: lineReady ? '100%' : '0%',
                background: 'linear-gradient(90deg, #A8894F, #E0C78A, #C8A96A)',
                boxShadow: '0 0 8px rgba(200,169,106,0.8)',
              }}
            />
          </span>

          <span
            className={`block text-white text-2xl sm:text-3xl md:text-4xl font-medium tracking-wide mt-2 transition-all duration-700 delay-300 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            in{' '}
            <span className="text-[#C8A96A]/80">{config.heroLocation}</span>
          </span>
        </h1>

        <p
          className={`text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mb-8 transition-all duration-700 delay-400 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {config.heroDesc}
        </p>

        <div
          className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-500 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <a
            href="tel:01529265060"
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] hover:from-[#E0C78A] hover:to-[#C8A96A] text-black font-bold px-8 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
          >
            <i className="ri-phone-fill text-lg" />
            Call 01529 265060
          </a>
          <button
            onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
            className="flex items-center justify-center gap-2 border-2 border-[#C8A96A] text-[#C8A96A] hover:bg-[#C8A96A]/10 font-bold px-8 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-list-3-line text-lg" />
            Free Quote
          </button>
        </div>

        <div
          className={`flex flex-wrap gap-6 mt-10 transition-all duration-700 delay-700 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {[
            { icon: 'ri-shield-check-fill', label: 'NAPIT Registered' },
            { icon: 'ri-star-fill', label: '5★ Google Reviews' },
            { icon: 'ri-map-pin-2-fill', label: 'Sleaford Based' },
            { icon: 'ri-file-certificate-fill', label: 'Part P Certified' },
          ].map(({ icon, label }) => (
            <div key={label} className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${icon} text-[#C8A96A] text-lg`} />
              </div>
              <span className="text-white/70 text-sm">{label}</span>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={() => document.getElementById('services-detail')?.scrollIntoView({ behavior: 'smooth' })}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#C8A96A]/60 hover:text-[#C8A96A] transition-colors cursor-pointer"
      >
        <span className="text-xs tracking-widest uppercase">Explore</span>
        <i className="ri-arrow-down-line text-xl" style={{ animation: 'bounce 2s ease-in-out infinite' }} />
      </button>
    </section>
  );
}
